import React from 'react';
import { useDashboard } from '../../hooks/useDashboard';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DollarSign, ClipboardList, ChefHat, AlertTriangle } from 'lucide-react';

const Dashboard: React.FC = () => {
  usePageTitle('Dashboard');
  const { data: stats, isLoading, error } = useDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-600 font-medium">Carregando estatísticas...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-danger-600 font-medium">Erro ao carregar dados do dashboard</div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      // Status de pedidos
      'Aguardando Pagamento': 'text-warning-700 bg-warning-50 border border-warning-200',
      'Pago': 'text-success-700 bg-success-50 border border-success-200',
      'Cancelado': 'text-danger-700 bg-danger-50 border border-danger-200',
      'Estornado': 'text-neutral-700 bg-neutral-100 border border-neutral-200',
      // Status de encomendas
      'Aguardando': 'text-primary-700 bg-primary-50 border border-primary-200',
      'EmProducao': 'text-secondary-700 bg-secondary-50 border border-secondary-200',
      'Pronto': 'text-primary-700 bg-primary-50 border border-primary-200',
      'Entregue': 'text-success-700 bg-success-50 border border-success-200',
    };
    return statusColors[status] || 'text-neutral-700 bg-neutral-100 border border-neutral-200';
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: { [key: string]: string } = {
      // Status de pedidos
      'Aguardando Pagamento': 'Aguardando Pgto.',
      'Pago': 'Pago',
      'Cancelado': 'Cancelado',
      'Estornado': 'Estornado',
      // Status de encomendas
      'Aguardando': 'Aguardando',
      'EmProducao': 'Em Produção',
      'Pronto': 'Pronto',
      'Entregue': 'Entregue',
    };
    return statusLabels[status] || status;
  };

  return (
    <div className="animate-fadeIn">
      <h1 className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-neutral-100 p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.01]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Vendas Hoje</p>
              <p className="text-3xl font-display font-bold text-neutral-900 mt-1">
                {formatCurrency(stats?.totalVendasHoje || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-success-50 to-success-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-neutral-100 p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.01]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Encomendas Hoje</p>
              <p className="text-3xl font-display font-bold text-neutral-900 mt-1">{stats?.encomendasHoje || 0}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-neutral-100 p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.01]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Em Produção</p>
              <p className="text-3xl font-display font-bold text-neutral-900 mt-1">{stats?.itensProducao || 0}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-neutral-100 p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.01]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Estoque Baixo</p>
              <p className="text-3xl font-display font-bold text-danger-600 mt-1">
                {stats?.ingredientesBaixoEstoque || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-danger-50 to-danger-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-white rounded-xl shadow-md border border-neutral-100 p-6">
          <h2 className="text-xl font-display font-bold text-neutral-900 mb-4">Últimas Vendas</h2>
          {stats?.ultimasVendas && stats.ultimasVendas.length > 0 ? (
            <div className="space-y-3">
              {stats.ultimasVendas.map((venda) => (
                <div
                  key={venda.id}
                  className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl hover:bg-neutral-100 transition-colors duration-150"
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-neutral-900">Pedido #{venda.id}</p>
                    <p className="text-xs text-neutral-600 mt-0.5">{formatDateTime(venda.dataHora)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2.5 py-1 text-xs font-medium rounded-lg ${getStatusColor(
                        venda.status
                      )}`}
                    >
                      {getStatusLabel(venda.status)}
                    </span>
                    <p className="text-sm font-bold text-neutral-900">
                      {formatCurrency(venda.valorTotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-neutral-500 text-center py-8 font-medium">Nenhuma venda registrada hoje</p>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md border border-neutral-100 p-6">
          <h2 className="text-xl font-display font-bold text-neutral-900 mb-4">Encomendas do Dia</h2>
          {stats?.encomendasDoDia && stats.encomendasDoDia.length > 0 ? (
            <div className="space-y-3">
              {stats.encomendasDoDia.map((encomenda) => (
                <div
                  key={encomenda.id}
                  className="flex items-center justify-between p-4 bg-neutral-50 rounded-xl hover:bg-neutral-100 transition-colors duration-150"
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-neutral-900">{encomenda.clienteNome}</p>
                    <p className="text-xs text-neutral-600 mt-0.5">
                      Entrega: {formatDateTime(encomenda.dataHoraEntrega)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2.5 py-1 text-xs font-medium rounded-lg ${getStatusColor(
                        encomenda.status
                      )}`}
                    >
                      {getStatusLabel(encomenda.status)}
                    </span>
                    <p className="text-sm font-bold text-neutral-900">
                      {formatCurrency(encomenda.valorTotal)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-neutral-500 text-center py-8 font-medium">Nenhuma encomenda para hoje</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
